import React from "react";
import { useMutation } from "@apollo/client";
import { gql } from "@apollo/client";
import { Form, FormSpy } from "react-final-form";
import arrayMutators from "final-form-arrays";
import { TextField, SwitchField, SelectField } from "@syos-music/form";

import { Grid, InputAdornment, MenuItem } from "@mui/material";
import { LoadingButton } from "@mui/lab";

import ShipmentContentField from "./ShipmentContentField";

const PACKAGING_WEIGHT = 21;
const KEY_RING_WEIGHT = 5;

const SHIPMENT_PURPOSES = {
  GIFT: "Cadeau",
  SAMPLE: "Echantillon commercial",
  GOODS: "Envoi commercial",
  DOCUMENT: "Document",
  OTHER: "Autre",
  RETURN: "Retour de marchandise",
};

const MUTATION = gql`
  mutation (
    $orderId: ID!
    $shipmentPurpose: CreateOrderShipmentColissimo_ShipmentPurpose!
    $content: [OrderShipmentContentInput]!
    $totalWeight: Int!
  ) {
    CreateOrderShipmentColissimo(
      orderId: $orderId
      shipmentPurpose: $shipmentPurpose
      content: $content
      totalWeight: $totalWeight
    ) {
      order {
        id
      }
    }
  }
`;

export default function OrderShipmentColissimoForm({ order, onSubmitted }) {
  const [createOrderShipmentColissimo] = useMutation(MUTATION);

  return (
    <Form
      noValidate
      mutators={{ ...arrayMutators }}
      onSubmit={({ shipmentPurpose, content, totalWeight }) =>
        createOrderShipmentColissimo({
          variables: {
            orderId: order.id,
            shipmentPurpose,
            content,
            totalWeight: parseInt(totalWeight),
          },
        })
          .then(() => {
            onSubmitted();
          })
          .catch(() => true)
      }
      initialValues={{
        shipmentPurpose: "GOODS",
        content: ShipmentContentField.getInitialValue(order),
        hasPackaging: true,
        hasKeyRing: false,
      }}
      keepDirtyOnReinitialize
      initialValuesEqual={() => true}
    >
      {({ form, handleSubmit, submitting, values }) => {
        function updateTotalWeight(values) {
          let totalWeight = values.content.reduce(
            (acc, { weight, quantity }) => acc + weight * quantity,
            0
          );

          if (values.hasPackaging) totalWeight += PACKAGING_WEIGHT;
          if (values.hasKeyRing) totalWeight += KEY_RING_WEIGHT;

          setTimeout(() => form.change("totalWeight", totalWeight));
        }

        if (values.totalWeight == null) updateTotalWeight(values);

        return (
          <form onSubmit={handleSubmit} noValidate>
            <Grid container justifyContent="flex-end">
              <Grid item xs={12}>
                <SelectField
                  name="shipmentPurpose"
                  required
                  label="Nature de l’envoi"
                >
                  {Object.entries(SHIPMENT_PURPOSES).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </SelectField>
              </Grid>

              <Grid item xs={12}>
                <ShipmentContentField name="content" order={order} />
              </Grid>

              <Grid item xs={12} sm={4}>
                <SwitchField
                  name="hasPackaging"
                  label="Emballage"
                  helperText={`+${PACKAGING_WEIGHT}g`}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <SwitchField
                  name="hasKeyRing"
                  label="Porte-clé"
                  helperText={`+${KEY_RING_WEIGHT}g`}
                />
              </Grid>

              <FormSpy
                subscription={{ values: true }}
                onChange={({ values: newValues }) => {
                  if (values.content.length !== newValues.content.length) {
                    updateTotalWeight(newValues);
                  } else {
                    newValues.content.forEach(
                      ({ weight: newWeight, quantity: newQuantity }, idx) => {
                        const { weight, quantity } = values.content[idx];

                        if (weight !== newWeight || quantity !== newQuantity)
                          updateTotalWeight(newValues);
                      }
                    );
                  }

                  if (values.hasPackaging !== newValues.hasPackaging)
                    updateTotalWeight(newValues);

                  if (values.hasKeyRing !== newValues.hasKeyRing)
                    updateTotalWeight(newValues);
                }}
              />

              <Grid item xs={12} sm={4}>
                <TextField
                  name="totalWeight"
                  type="number"
                  inputProps={{ min: 0, step: 1 }}
                  required
                  label="Poids total"
                  // eslint-disable-next-line react/jsx-no-duplicate-props
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">g</InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item>
                <LoadingButton type="submit" loading={submitting}>
                  Valider
                </LoadingButton>
              </Grid>
            </Grid>
          </form>
        );
      }}
    </Form>
  );
}

OrderShipmentColissimoForm.fragments = {
  order: gql`
    fragment OrderShipmentColissimoForm_order on Order {
      id
      ...ShipmentContentField_Order
    }

    ${ShipmentContentField.fragments.order}
  `,
};
